import React, { useReducer, useEffect, useContext } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Link } from 'react-router-dom';
import { PermissionsContext } from '../../UIContainer/PermissionsWrapper';
import { AzureSearchServiceMenuProps } from '../Model/AzureSearchServiceProps';
import { azureSearchServiceService } from '../AzureSearchServiceService';
import {
  MenuType,
  AzureSearchServiceMenuItems,
} from '../Model/AzureSearchServiceModel';

const menuItems: AzureSearchServiceMenuItems[] = [
  {
    id: 'edit',
    label: 'Edit',
    getLink: (name: string) => `/SearchService/${name}/edit`,
    validationFunc: MenuType.Edit,
  },
  {
    id: 'delete',
    label: 'Delete',
    validationFunc: MenuType.Delete,
  },
];

enum ActionTypes {
  OPEN_MENU = 'OPEN_MENU',
  CLOSE_MENU = 'CLOSE_MENU',
  SET_DELETE_PERMISSION = 'SET_DELETE_PERMISSION',
}

type StateType = {
  anchor: HTMLButtonElement | null;
  menuOpen: boolean;
  canDelete: boolean;
};

type ActionType =
  | { type: ActionTypes.OPEN_MENU; payload: HTMLButtonElement }
  | { type: ActionTypes.CLOSE_MENU }
  | { type: ActionTypes.SET_DELETE_PERMISSION; payload: boolean };

const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case ActionTypes.OPEN_MENU:
      return { ...state, menuOpen: true, anchor: action.payload };
    case ActionTypes.CLOSE_MENU:
      return { ...state, menuOpen: false, anchor: null };
    case ActionTypes.SET_DELETE_PERMISSION:
      return { ...state, canDelete: action.payload };
    default:
      return state;
  }
};

const AzureSearchServiceMenu: React.FC<AzureSearchServiceMenuProps> = ({
  Name,
  DeleteFunction,
}) => {
  const permissions = useContext(PermissionsContext);
  const [state, dispatch] = useReducer(reducer, {
    anchor: null,
    menuOpen: false,
    canDelete: false,
  });

  useEffect(() => {
    const checkDeletePermission = async () => {
      const result =
        await azureSearchServiceService.canDeleteAzureSearchService(Name);
      dispatch({ type: ActionTypes.SET_DELETE_PERMISSION, payload: result });
    };
    checkDeletePermission();
  }, [Name]);

  const hasWriteAccess = () => Boolean(permissions['AzureSearchServiceWriter']);

  const isActionAllowed = (type: 'edit' | 'delete'): boolean => {
    const validations: Record<'edit' | 'delete', () => boolean> = {
      edit: () => hasWriteAccess(),
      delete: () => hasWriteAccess() && state.canDelete,
    };
    return validations[type]();
  };

  return (
    <div>
      <IconButton
        aria-label="More"
        aria-haspopup="true"
        onClick={(e) =>
          dispatch({ type: ActionTypes.OPEN_MENU, payload: e.currentTarget })
        }
        size="large"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="azure-search-service-menu"
        anchorEl={state.anchor}
        open={state.menuOpen}
        onClose={() => dispatch({ type: ActionTypes.CLOSE_MENU })}
      >
        {menuItems
          .filter((menu) => isActionAllowed(menu.validationFunc))
          .map((menu) => (
            <MenuItem
              key={menu.id}
              component={menu.getLink ? Link : 'div'}
              to={menu.getLink ? menu.getLink(Name) : undefined}
              onClick={menu.getLink ? undefined : () => DeleteFunction(Name)}
            >
              {menu.label}
            </MenuItem>
          ))}
      </Menu>
    </div>
  );
};

export default AzureSearchServiceMenu;
